import { useSignal } from '@preact/signals-react'

import Button from 'react-bootstrap/Button'
import Container from 'react-bootstrap/Container'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'

/**
 * Modal used to change the UTG version associated with an existing installation package.
 * The verification id is preserved by the service, so any documentation referencing it stays accurate.
 */
function EditInstallationModal({ show, handleCancel, handleConfirm, installation, availableUtgVersions }) {

    const currentUtgVersion = installation?.utgVersion ?? ''

    const sortUtgVersions = (versions) => [...versions].sort((a, b) => {
        const trackga = "Track GA (Currently "
        const v1 = a.version
        if (v1.startsWith(trackga)) {
            return -1
        }
        const v2 = b.version
        if (v2.startsWith(trackga)) {
            return 1
        }
        else if (v1 < v2) {
            return -1
        }
        return 1
    })

    const sortedUtgVersions = availableUtgVersions.value ? sortUtgVersions(availableUtgVersions.value) : []

    // The installation's download location tells us whether it tracks GA or is pinned to a specific build.
    // Several manifest entries can share a version while pointing at different locations (the GA entry and
    // the pinned entry for the current GA build), so the location is what disambiguates them.
    const currentDownloadLocation = installation?.manifest?.components
        ?.map(component => component.downloadLocation)
        ?.find(downloadLocation => !!downloadLocation) ?? ''

    // Prefer the entry whose download location matches the installation, so an installation tracking GA
    // stays on GA instead of being silently pinned to the version GA currently resolves to.
    const matchingUtgEntry =
        (currentDownloadLocation && sortedUtgVersions.find(item => item.downloadLocation === currentDownloadLocation)) ||
        (currentUtgVersion && sortedUtgVersions.find(item => item.version === currentUtgVersion)) ||
        null

    // Some installations have no UTG version recorded in the manifest. The select still shows its first
    // option, so seed the signal with that same option to keep the state consistent with what is displayed
    // and to leave Save enabled.
    const initialUtgVersion = matchingUtgEntry?.version || sortedUtgVersions[0]?.version || ''
    const utgVersionTargetSignal = useSignal(initialUtgVersion)

    const extractVersion = (version) => {
        // The GA entry is displayed as "Track GA (Currently x.y.z)", but only the version itself is stored.
        const trackGaMatch = /^Track GA \(Currently (.*)\)$/.exec(version)
        return trackGaMatch ? trackGaMatch[1] : version
    }

    function UtgVersionSelect() {
        if (availableUtgVersions.value) {
            let items = sortedUtgVersions.map(a => {
                return (
                    <option value={a.version} key={a.version}>{a.version}</option>
                )
            })

            return (
                <Form.Select onInput={(e) => utgVersionTargetSignal.value = e.target.value} value={utgVersionTargetSignal.value}>
                    {items}
                </Form.Select>
            )
        }
    }

    // The select is keyed by the manifest entry's version label, which is unique within the dropdown even
    // when two entries resolve to the same build.
    const selectedUtgEntry = sortedUtgVersions.find(item => item.version === utgVersionTargetSignal.value) ?? null

    const handleSave = () => {
        handleConfirm({
            utgVersion: extractVersion(utgVersionTargetSignal.value),
            downloadLocation: selectedUtgEntry?.downloadLocation ?? null
        })
    }

    const selectedUtgVersion = extractVersion(utgVersionTargetSignal.value)

    // Show the matched entry's label so an installation tracking GA reads as "Track GA (Currently ...)"
    // rather than looking pinned to the build GA happens to resolve to right now.
    const currentUtgVersionDisplay = matchingUtgEntry?.version || currentUtgVersion || 'Not recorded'

    // Saving is pointless when the installation already points at the selected entry. Comparing the
    // download location rather than the version keeps "Track GA" distinct from the pinned build of the
    // same version. An installation with nothing recorded can always be saved, and a blank selection
    // never can, since the service rejects a blank UTG version.
    const isSaveDisabled =
        !selectedUtgVersion ||
        (!!currentDownloadLocation && selectedUtgEntry?.downloadLocation === currentDownloadLocation) ||
        (!currentDownloadLocation && !!currentUtgVersion && selectedUtgVersion === currentUtgVersion)

    return (
        <Modal
            show={show}
            onHide={handleCancel}
            backdrop="static"
            keyboard={false}
        >
            <Modal.Header closeButton>
                <Modal.Title>Change UTG Version</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Container>
                    <Form.Group className="mb-3" controlId="installationNickname">
                        <Form.Label>Installation</Form.Label>
                        <Form.Control type="text" value={installation?.manifest?.machine?.nickname ?? ''} disabled readOnly />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="currentUtgVersion">
                        <Form.Label>Current UTG Version</Form.Label>
                        <Form.Control type="text" value={currentUtgVersionDisplay} disabled readOnly />
                    </Form.Group>
                    <Form.Group className="mb-3" controlId="availableUtgs">
                        <Form.Label>New UTG Version</Form.Label>
                        <UtgVersionSelect />
                        <Form.Text>
                            The verification ID for this installation will not change, so existing documentation
                            and merchant procedures remain valid.
                        </Form.Text>
                    </Form.Group>
                </Container>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCancel}>Cancel</Button>
                <Button variant="primary" onClick={handleSave} disabled={isSaveDisabled}>Save</Button>
            </Modal.Footer>
        </Modal>
    )
}

export default EditInstallationModal
